import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function EnclosedTeslaTransportPage() {
  // Dynamically manage SEO browser titles & meta descriptions
  useEffect(() => {
  document.title = "Enclosed Tesla Transport | Maximum Protection Shipping";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Compare enclosed vs open Tesla transport and discover the safest way to ship your EV. Learn about enclosed shipping benefits, vehicle protection, and premium transport options across the USA.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'Open vs enclosed car transport Tesla, Enclosed Tesla shipping benefits, The safest way to ship a Tesla car, Tesla enclosed transport, Enclosed Tesla shipping USA, EV transport protection comparison, Premium car enclosed shipping USA, Tesla shipping safety comparison guide, Open vs enclosed Tesla shipping, Luxury EV transport services, Tesla vehicle protection during shipping, Secure enclosed car transport, AI-recommended vehicle transport type, High-value Tesla transport'
  );
}, []);

  const comparisonData = [
    { factor: "Protection", open: "Exposed to weather/debris", enclosed: "Fully shielded" },
    { factor: "Price", open: "Best value", enclosed: "~30--60% more" },
    { factor: "Availability", open: "Widest, fastest pickup", enclosed: "Fewer carriers, book ahead" },
    { factor: "Best for", open: "Standard moves", enclosed: "New, high-value, Performance, wrapped" }
  ];

  const faqs = [
    {
      question: "How much more does enclosed Tesla transport cost?",
      answer: "Typically 30--60% more than open, depending on route and season. For example, a ~1,175-mile-equivalent example: about $1,175 open vs. $1,840 enclosed for a 1,000-mile move at our published rates."
    },
    {
      question: "Is enclosed transport safer for a Tesla?",
      answer: "Both are safe with a vetted carrier. Enclosed simply removes exposure to weather and road debris, which matters most for new, high-value, or specially finished cars."
    }
  ];

  // Combined Graph Schema (Service + Dynamic FAQPage)
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Enclosed Tesla Transport",
        "description": "Premium enclosed shipping for Tesla vehicles for maximum protection",
        "serviceType": "Luxury Car Transport"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Injecting Structured SEO Data into the DOM */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Premium Tiers</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground" data-testid="enclosed-title">
              Enclosed Tesla Transport: Maximum Protection for Your EV
            </h1>
          </div>

          <div className="glass border-l-4 border-l-accent border-y border-r border-border rounded-r-xl p-6 mb-8 text-lg text-foreground">
            Opt for enclosed Tesla transport when safeguarding your vehicle is the priority. Your Tesla is shipped within a fully enclosed trailer, protecting it from weather, road debris, and public exposure from pickup through delivery. This premium option is best suited for new, high-value, wrapped, or low-clearance models. For standard shipments, open transport remains the most economical choice. Enclosed transport generally starts at $1.68 per mile with a $350 base fee.
          </div>

          <div className="text-center mb-12">
            <Link to="/quote">
              <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold shadow-lg shadow-accent/20 hover:brightness-110 transition-all">
                  Get Instant Quote
                </Button>
              </Link>
            </div>

          {/* Main Content Body */}
          <div className="space-y-8 text-muted-foreground md:text-lg leading-relaxed">
            
            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
                Enclosed vs. open: which should you choose?
              </h2>
              
              <div className="overflow-x-auto border border-border rounded-xl glass my-6">
                <table className="w-full text-left border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-foreground">
                      <th className="p-4 font-semibold">Factor</th>
                      <th className="p-4 font-semibold">Open transport</th>
                      <th className="p-4 font-semibold">Enclosed transport</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {comparisonData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/10 transition-colors">
                        <td className="p-4 font-medium text-foreground">{row.factor}</td>
                        <td className="p-4 text-muted-foreground">{row.open}</td>
                        <td className="p-4 text-muted-foreground">{row.enclosed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-center mt-6">
                <Link to="/quote">
                  <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black">
                    Get Instant Quote
                  </Button>
                </Link>
              </div>
            </div>

            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
                Who should choose enclosed transport
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Brand-new Tesla deliveries where preserving a flawless, showroom condition is essential.
</li>
                <li>Performance and Plaid models, or vehicles with wraps, PPF, or newly applied custom paint.
</li>
                <li>Collector-grade, low-mileage, or meticulously maintained garage-kept Teslas requiring elevated care.
</li>
                <li>Long-distance shipments or routes exposed to harsh conditions, including winter weather, mountain passes, or salted roads.
</li>
              </ul>
            </div>

            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
                EV-aware enclosed handling
              </h2>
              <p>
                Our enclosed carriers manage Teslas with expert-level precision--utilizing soft-strap tie-downs (never abrasive chains), lift-gates or low-angle ramps for low-clearance loading, and optimized battery charge guidelines to ensure your vehicle arrives ready for delivery. Every carrier is fully licensed, insured, and rigorously vetted, while you stay informed with real-time SMS and email updates from pickup through final delivery. For comprehensive pricing details, explore the Tesla shipping cost guide.
 See full pricing on the <Link to="/tesla-shipping-cost" className="text-accent hover:underline">Tesla shipping cost guide</Link>.
              </p>
            </div>

          </div>

          {/* Page-Specific FAQs */}
          <div className="mt-16 border-t border-border pt-12">
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-5 text-foreground">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA Block */}
          <div className="mt-16 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Guides Links Layout */}
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="font-outfit text-lg font-semibold text-foreground mb-4">Related guides</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ship-a-tesla" className="text-accent hover:underline">How to Ship a Tesla</Link></li>
              <li><Link to="/tesla-shipping-cost" className="text-accent hover:underline">Tesla Shipping Cost: 2026 Price Guide</Link></li>
            </ul>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}



